/**
 * The back-solve panel — Resource Plan Epic 5 (RP-5.1, RP-5.2, RP-5.3), M3.
 *
 * Three questions a planner asks before they negotiate, and a diff they apply or discard.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * APPLY OR DISCARD — NOTHING AUTO-APPLIES
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-5.1: "Result is a proposal with a diff against current hours; the planner applies or
 * discards. Nothing auto-applies." So the panel has TWO buttons and the first one writes
 * nothing: Solve fetches a proposal, the diff renders, and Apply is a separate,
 * deliberate act. The proposal is cleared whenever an input changes, so a stale diff can
 * never be applied against numbers the planner has since edited.
 *
 * A BLOCKED proposal renders its reason and hides Apply entirely — RP-5.1's "A negative
 * residual shows the shortfall and does not apply".
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * NO COST ON THIS PANEL, INCLUDING FOR [RM]
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-5.3's target-margin solve consumes cost server-side and returns a PRICE. Nothing in
 * `PlanSolveProposalView` carries a cost figure at any grain, so there is nothing here to
 * hide behind `plan.canViewCost` — see the header of src/api/plan-solvers.ts. Do not add
 * a cost column "for RM"; the margin panel is where cost lives.
 */

import { ArrowRight, Lock } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  type PlanSolveRequest,
  useApplyPlanSolve,
  usePlanSolve,
} from "@/hooks/use-plan-composition";
import { cn } from "@/lib/utils";
import type { ResourcePlanDetailView } from "@/schemas/resource-plans";

type SolveKind = "budget" | "hours" | "targetMargin";

const KINDS: Array<[SolveKind, string, string]> = [
  ["budget", "From a budget", "How many hours can I quote for this much?"],
  ["hours", "From total hours", "Spread a rough total across the plan's mix."],
  ["targetMargin", "For a target margin", "What must I charge to hit this margin?"],
];

/**
 * A ramped plan's figures carry a small rounding residue, and saying so beats leaving a
 * planner to find it.
 *
 * Each line is priced at `rate × its average ramp`, rounded to whole cents once — so the
 * solve's own view of current revenue can sit ~0.04% away from the header's, which ramps
 * every CELL. On an unramped plan the two are identical and this note does not render.
 */
function RampedFigureNote({ plan }: { plan: ResourcePlanDetailView }) {
  if (plan.ramped !== true) return null;
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      This plan is ramped, so these figures are computed at each line&rsquo;s effective rate and can
      differ from the header by a fraction of a percent. The proposal&rsquo;s own revenue figure is
      exact — it is what the plan will report once applied.
    </p>
  );
}

export function SolvePanel({ plan }: { plan: ResourcePlanDetailView }) {
  const solve = usePlanSolve(plan.id);
  const apply = useApplyPlanSolve(plan.id);

  const [kind, setKind] = useState<SolveKind>("budget");
  const [value, setValue] = useState("");

  const proposal = solve.data ?? null;
  const lockedCount = plan.lines.filter((l) => l.locked === true).length;

  /** Clear any proposal the moment an input changes — a stale diff must not be applicable. */
  function change(next: () => void): void {
    solve.reset();
    next();
  }

  const request: PlanSolveRequest | null =
    value.trim() === ""
      ? null
      : kind === "budget"
        ? { kind, targetRevenue: value.trim() }
        : kind === "hours"
          ? { kind, targetHours: value.trim() }
          : { kind, targetMarginPercent: value.trim() };

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-card p-4 shadow-xs">
        <h3 className="font-display text-base font-semibold">Solve</h3>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Work backwards from a number. Every solve returns a proposal you can read before you apply
          it — nothing changes the grid until you say so.
        </p>

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          {KINDS.map(([key, label, hint]) => (
            <button
              key={key}
              type="button"
              onClick={() => change(() => setKind(key))}
              className={cn(
                /*
                 * `transition-colors`, NOT `t-dropdown`.
                 *
                 * These are radio-like cards, not a dropdown, and `.t-dropdown` in
                 * styles/globals.css is the CLOSED half of a two-state animation:
                 * `opacity: 0; pointer-events: none` until an `is-open` class or a Radix
                 * `data-state="open"` reveals it. With only the base class all three cards
                 * rendered fully laid out and completely invisible — so RP-5.2's hours
                 * solve and RP-5.3's target-margin solve could not be selected at all,
                 * while the panel looked merely empty rather than broken.
                 *
                 * Found in M4 while building the export menu, which hit the same trap.
                 */
                "rounded-md border p-3 text-left transition-colors",
                kind === key
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-input hover:bg-muted/40",
              )}
            >
              <span className="block text-sm font-medium">{label}</span>
              <span className="mt-0.5 block text-sm text-muted-foreground">{hint}</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-sm">
              {kind === "budget"
                ? plan.budgetCadence === "Monthly"
                  ? "Monthly budget ($)"
                  : "Total budget ($)"
                : kind === "hours"
                  ? "Total hours"
                  : "Target margin (%)"}
            </Label>
            <Input
              inputMode="decimal"
              value={value}
              onChange={(e) => change(() => setValue(e.target.value))}
              placeholder={kind === "targetMargin" ? plan.marginFloorPercent : "0.00"}
              className="mt-1 h-8 w-40 text-sm"
            />
          </div>
          <Button
            size="sm"
            disabled={request === null || solve.isPending}
            onClick={() => request !== null && solve.mutate(request)}
          >
            {solve.isPending ? "Solving…" : "Solve"}
          </Button>
          {lockedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Lock className="h-3 w-3" />
              {lockedCount} locked line{lockedCount === 1 ? "" : "s"} will hold constant
            </span>
          )}
        </div>
      </div>

      {proposal !== null && (
        <div className="rounded-lg border border-border bg-card p-4 shadow-xs">
          {proposal.blocked !== null ? (
            <>
              <p className="text-sm font-medium text-error-700">Cannot solve this</p>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{proposal.blocked}</p>
              {proposal.shortfall !== null && (
                <p className="mt-2 font-mono text-sm text-error-700">
                  Over by ${proposal.shortfall}
                </p>
              )}
            </>
          ) : proposal.kind === "targetMargin" ? (
            <TargetMarginResult plan={plan} proposal={proposal} />
          ) : (
            <>
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <div>
                  <p className="text-sm font-medium">Proposal</p>
                  <p className="mt-0.5 font-mono text-sm text-muted-foreground">
                    {proposal.totalHours} hours · ${proposal.totalRevenue}
                    {proposal.blendedRate !== null && ` · $${proposal.blendedRate}/hr blended`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => solve.reset()}>
                    Discard
                  </Button>
                  <Button
                    size="sm"
                    disabled={!plan.canEdit || apply.isPending || request === null}
                    onClick={() =>
                      request !== null && apply.mutate(request, { onSuccess: () => solve.reset() })
                    }
                  >
                    {apply.isPending ? "Applying…" : "Apply to the grid"}
                  </Button>
                </div>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[520px] text-sm">
                  <thead className="text-left">
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="py-1.5 font-medium">Plan Line</th>
                      <th className="py-1.5 text-right font-medium">Now</th>
                      <th className="w-8 py-1.5" />
                      <th className="py-1.5 text-right font-medium">Proposed</th>
                      <th className="py-1.5 text-right font-medium">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposal.changes.map((change) => (
                      <tr key={change.lineId} className="border-b border-border last:border-0">
                        <td className="py-1.5">
                          <span className="inline-flex items-center gap-1.5">
                            {change.locked && (
                              <Lock
                                className="h-3 w-3 text-muted-foreground"
                                aria-label="Locked — held constant"
                              />
                            )}
                            {change.clientLaborCategory ?? change.laborCategoryName}
                          </span>
                        </td>
                        <td className="py-1.5 text-right font-mono text-muted-foreground">
                          {change.fromHours}
                        </td>
                        <td className="py-1.5 text-center">
                          <ArrowRight className="mx-auto h-3 w-3 text-muted-foreground" />
                        </td>
                        <td className="py-1.5 text-right font-mono">{change.toHours}</td>
                        <td
                          className={cn(
                            "py-1.5 text-right font-mono",
                            Number(change.deltaHours) > 0
                              ? "text-success-700 dark:text-success-400"
                              : Number(change.deltaHours) < 0
                                ? "text-error-700"
                                : "text-muted-foreground",
                          )}
                        >
                          {Number(change.deltaHours) > 0 ? "+" : ""}
                          {change.deltaHours}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Applying rewrites each unlocked line&rsquo;s cells, spread evenly across the months
                its Workstream covers. Every cell stays editable afterwards.
                {plan.ramped === true &&
                  " The hours above are what goes in the cells; the revenue is what the plan will bill once the ramp is applied."}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * RP-5.3's answer: a price, and what an hour has to average to reach it.
 *
 * There is no Apply here. The Contracted Fee is an INPUT sourced from sales and "never
 * back-solved from hours" (RP-1.2 BC) — so this panel hands the planner a number to
 * negotiate with, and the fee is typed onto the plan when sales agree it.
 */
function TargetMarginResult({
  plan,
  proposal,
}: {
  plan: ResourcePlanDetailView;
  proposal: {
    requiredRevenue?: string;
    impliedBlendedRate?: string | null;
    uplift?: string;
    currentMarginPercent?: string | null;
  };
}) {
  const uplift = Number(proposal.uplift ?? "0");

  return (
    <>
      <p className="text-sm font-medium">To hit that margin on this plan&rsquo;s hours</p>
      <div className="mt-3 grid gap-4 sm:grid-cols-3">
        <div>
          <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Price
          </span>
          <span className="mt-0.5 block font-mono text-lg font-semibold">
            ${proposal.requiredRevenue}
          </span>
        </div>
        <div>
          <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Implied Blended Rate
          </span>
          <span className="mt-0.5 block font-mono text-lg font-semibold">
            {proposal.impliedBlendedRate === null || proposal.impliedBlendedRate === undefined
              ? "—"
              : `$${proposal.impliedBlendedRate}`}
          </span>
        </div>
        <div>
          <span className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {uplift >= 0 ? "Increase needed" : "Room to concede"}
          </span>
          <span
            className={cn(
              "mt-0.5 block font-mono text-lg font-semibold",
              uplift > 0 ? "text-error-700" : "text-success-700 dark:text-success-400",
            )}
          >
            ${Math.abs(uplift).toFixed(2)}
          </span>
        </div>
      </div>

      <p className="mt-3 text-sm text-muted-foreground">
        This plan currently makes{" "}
        <span className="font-mono">{proposal.currentMarginPercent ?? "—"}%</span> at its applied
        rates, against a {plan.marginFloorPercent}% floor. The number above is what to negotiate
        with — the Contracted Fee comes from sales and is entered on the plan, never derived from
        its own hours.
      </p>
      <RampedFigureNote plan={plan} />
    </>
  );
}
